import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { FormSubmission } from "@/types/formSubmissions";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface FormValidationCardProps {
  submission: FormSubmission;
  onUpdate: () => void;
}

export function FormValidationCard({ submission, onUpdate }: FormValidationCardProps) {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleValidateForm = async (isValid: boolean) => {
    setIsUpdating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('form_submissions')
        .update({
          form_validated: isValid,
          validated_by: user?.id,
          validated_at: new Date().toISOString(),
          validation_status: getNewStatus(isValid, submission.logo_validated),
        })
        .eq('id', submission.id);

      if (error) throw error;

      // Update entity if both are validated
      if (isValid && submission.logo_validated && submission.entity_id) {
        await updateEntity(submission.entity_id, true, true);
      }

      toast({
        title: isValid ? "Formulário aprovado" : "Formulário rejeitado",
        description: `${submission.company_name}`,
      });

      onUpdate();
    } catch (error) {
      console.error('Error validating form:', error);
      toast({
        title: "Erro",
        description: "Não foi possível validar o formulário",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleValidateLogo = async (isValid: boolean) => {
    setIsUpdating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('form_submissions')
        .update({
          logo_validated: isValid,
          validated_by: user?.id,
          validated_at: new Date().toISOString(),
          validation_status: getNewStatus(submission.form_validated, isValid),
        })
        .eq('id', submission.id);

      if (error) throw error;

      // Update entity if both are validated
      if (isValid && submission.form_validated && submission.entity_id) {
        await updateEntity(submission.entity_id, true, true);
      }

      toast({
        title: isValid ? "Logo aprovada" : "Logo rejeitada",
        description: `${submission.company_name}`,
      });

      onUpdate();
    } catch (error) {
      console.error('Error validating logo:', error);
      toast({
        title: "Erro",
        description: "Não foi possível validar a logo",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const updateEntity = async (entityId: number, formValidated: boolean, logoValidated: boolean) => {
    await supabase
      .from('entities')
      .update({
        form_validated: formValidated,
        logo_validated: logoValidated,
        validation_date: new Date().toISOString(),
      })
      .eq('id', entityId);
  };

  const getNewStatus = (formValid: boolean, logoValid: boolean): string => {
    if (formValid && logoValid) return 'approved';
    if (!formValid || !logoValid) return 'rejected';
    return 'pending';
  };

  const getStatusBadge = () => {
    switch (submission.validation_status) {
      case 'approved':
        return <Badge className="bg-green-600">Aprovado</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejeitado</Badge>;
      default:
        return <Badge variant="secondary">Pendente</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{submission.company_name}</CardTitle>
            <div className="flex gap-2 mt-2 text-sm text-muted-foreground">
              <span>{submission.state}</span>
              {submission.region && <span>• {submission.region}</span>}
              <span>• {submission.entity_type}</span>
            </div>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!submission.entity_id && (
          <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-950 rounded-md">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-yellow-800 dark:text-yellow-200">
              Entidade não encontrada no sistema
            </span>
          </div>
        )}

        {/* Form Validation */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">Formulário</span>
              {submission.form_validated && (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              )}
            </div>
            {submission.form_link && (
              <Button
                variant="ghost"
                size="sm"
                asChild
              >
                <a href={submission.form_link} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            )}
          </div>
          {!submission.form_validated && (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handleValidateForm(true)}
                disabled={isUpdating}
                className="flex-1"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Aprovar
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleValidateForm(false)}
                disabled={isUpdating}
                className="flex-1"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Rejeitar
              </Button>
            </div>
          )}
        </div>

        {/* Logo Validation */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">Logo</span>
              {submission.logo_validated && (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              )}
            </div>
            {submission.logo_link && (
              <Button
                variant="ghost"
                size="sm"
                asChild
              >
                <a href={submission.logo_link} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            )}
          </div>
          {!submission.logo_validated && (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handleValidateLogo(true)}
                disabled={isUpdating}
                className="flex-1"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Aprovar
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleValidateLogo(false)}
                disabled={isUpdating}
                className="flex-1"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Rejeitar
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
